// Issuing an invoice. One component, two places: the matter's billing card
// and the firm-wide billing screen, which picks the matter first.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createInvoice, type CaseInvoice } from "../api/caseInvoices";

interface Props {
  /** Mongo _id of the Case the invoice is issued against. */
  caseId: string;
  /** The client's address, when the firm holds one. Without it there is
      nothing to offer: the server would answer that it sent nothing. */
  clientEmail?: string;
  /** What the caller does with the invoice - close a form, open its page. */
  onIssued?: (invoice: CaseInvoice, emailSent: boolean) => void;
  onCancel?: () => void;
}

export function InvoiceForm({ caseId, clientEmail, onIssued, onCancel }: Props) {
  const canEmail = !!clientEmail?.trim();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const issue = useMutation({
    // Keyed so a dialog above this form can lock itself while the issue
    // is in flight - the pending flag lives down here.
    mutationKey: ["case-invoices", caseId, "issue"],
    mutationFn: (input: Parameters<typeof createInvoice>[1]) => createInvoice(caseId, input),
    onSuccess: async ({ invoice, emailSent }) => {
      setAmount("");
      setDescription("");
      setDueDate("");
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["case-invoices", caseId] }),
        // The firm ledger and its totals are readers of this write as
        // much as the matter is, and they hold their answer for a minute.
        queryClient.invalidateQueries({ queryKey: ["billing"] }),
      ]);
      onIssued?.(invoice, emailSent);
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Could not create it."),
  });

  const submit = () => {
    const dollars = Number(amount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setError("Enter the amount in dollars.");
      return;
    }
    if (!description.trim()) {
      setError("A short service description is required.");
      return;
    }
    issue.mutate({
      amountCents: Math.round(dollars * 100),
      description: description.trim(),
      ...(dueDate ? { dueDate } : {}),
      notifyClient: canEmail && notify,
    });
  };

  return (
    <div className="cc-form-panel">
      <div className="cc-form-row">
        <label className="cc-form-field" style={{ width: 150 }}>
          <span className="cc-form-label">Amount</span>
          <span className="cc-money-input">
            <span aria-hidden="true">$</span>
            <input
              className="cc-field tnum"
              aria-label="Amount in dollars"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
            />
          </span>
        </label>

        <label className="cc-form-field" style={{ flex: 1, minWidth: 240 }}>
          <span className="cc-form-label">What it is for</span>
          <input
            className="cc-field"
            aria-label="Service description"
            placeholder="Representation through the master hearing"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <span className="cc-form-hint">The client reads this line on the invoice.</span>
        </label>

        <label className="cc-form-field" style={{ width: 165 }}>
          <span className="cc-form-label">Due date</span>
          <input
            className="cc-field"
            aria-label="Due date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <span className="cc-form-hint">Optional.</span>
        </label>
      </div>

      <div className="cc-form-actions">
        {canEmail ? (
          <label className="cc-form-check">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            Email it to {clientEmail}
          </label>
        ) : (
          <span className="cc-form-hint">
            No email on file for this client - the invoice is issued without one.
          </span>
        )}
        <span style={{ flex: 1 }} />
        {onCancel && (
          <button type="button" className="cc-link-btn" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="cc-btn-primary"
          disabled={issue.isPending}
          onClick={submit}
        >
          {issue.isPending ? "Issuing..." : "Issue invoice"}
        </button>
      </div>

      {error && (
        <div role="alert" className="cc-form-error">
          {error}
        </div>
      )}
    </div>
  );
}
